import React, { useEffect, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Phone, Mail, MapPin, MessageCircle, Building2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { WeChatButton } from "@/components/WeChatButton";

export default function ContactPage() {
  const { tr, settings, lang } = useApp();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.title = "Contact | AFC TELECOM"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
      toast.error(lang === "ar" ? "املأ الحقول المطلوبة" : "Please fill required fields");
      return;
    }
    setLoading(true);
    try {
      await api.post("/inquiries", { ...form, kind: "contact" });
      toast.success(lang === "ar" ? "تم إرسال الرسالة" : "Inquiry sent — our team will reply shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error(lang === "ar" ? "فشل الإرسال" : "Could not send. Please try WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const waLink = `https://wa.me/${settings.whatsapp_sales}?text=${encodeURIComponent(tr.whatsappMsgs.general)}`;

  const contacts = [
    { role: lang === "ar" ? "الإدارة" : "Management", name: settings.management_name || "Hayder Alsaadi", phone: settings.management_phone || "+971 54 549 9994", wechat: settings.wechat_management || "Hayder-Alsaadi1", primary: true, testid: "contact-mgmt-1" },
    { role: lang === "ar" ? "الإدارة" : "Management", name: settings.management2_name || "Ahmed Al Fukiki", phone: settings.management2_phone || "+971 50 256 2525", wechat: null, testid: "contact-mgmt-2" },
    { role: lang === "ar" ? "مبيعات ومشتريات" : "Sales & Purchasing", name: settings.sales_name || "Taha Alzaki", phone: settings.sales_phone || "+971 52 976 1348", wechat: settings.wechat_sales || "Taha-alzaki-92", testid: "contact-sales" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      <div className="mb-12">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1E5FD9]">{lang === "ar" ? "تواصل" : "Reach the Desk"}</p>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold text-[#0B132B]">{tr.nav.contact}</h1>
        <p className="mt-3 text-sm text-slate-600 max-w-2xl">
          {lang === "ar"
            ? "فريق مبيعات B2B الخاص بنا جاهز لتقديم عروض الأسعار بالجملة والإجابة على استفسارات التوزيع."
            : "Our Dubai B2B desk is ready to answer wholesale quotes, authorized distribution questions, and shipping requests."}
        </p>
      </div>

      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8">
        <div className="space-y-6">
          {/* Legal + address card */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="contact-legal-card">
            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 text-[#1E5FD9] mt-0.5" />
              <div>
                <p className="font-display text-xl font-semibold text-[#0B132B]">{settings.legal_name || "ALFUKIKI TRADING FZCO"}</p>
                <p dir="rtl" className="text-sm text-slate-600 mt-1">{settings.legal_name_ar || "الفكيكي للتجارة ش.م.ح"}</p>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                  5WA 206, Second Floor<br />
                  5 West A<br />
                  Dubai Airport Freezone, Dubai, UAE
                </p>
                <p className="mt-3 inline-flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-[#1E5FD9]" />
                  <a href={`mailto:${settings.email_sales}`} className="text-[#1E5FD9] hover:underline">{settings.email_sales}</a>
                </p>
              </div>
            </div>
          </div>

          {/* Contacts */}
          <div className="grid sm:grid-cols-2 gap-4">
            {contacts.map((c) => (
              <div
                key={c.testid}
                className={`rounded-2xl p-5 border ${c.primary ? "bg-[#0B132B] text-white border-[#1E5FD9]" : "bg-white border-slate-200"}`}
                data-testid={c.testid}
              >
                <p className={`text-xs uppercase tracking-[0.2em] font-semibold ${c.primary ? "text-[#4785FF]" : "text-[#1E5FD9]"}`}>{c.role}</p>
                <p className={`mt-1 font-display text-lg font-semibold ${c.primary ? "text-white" : "text-[#0B132B]"}`}>{c.name}</p>
                <a href={`tel:${c.phone.replace(/\s/g, "")}`} className={`mt-3 inline-flex items-center gap-2 text-sm ${c.primary ? "text-slate-200 hover:text-white" : "text-slate-700 hover:text-[#1E5FD9]"}`}>
                  <Phone className="w-4 h-4" /> {c.phone}
                </a>
                {c.wechat && (
                  <div className="mt-1">
                    <WeChatButton id={c.wechat} dark={c.primary} testid={`${c.testid}-wechat`} />
                  </div>
                )}
                {c.primary && (
                  <a
                    href={`https://wa.me/${c.phone.replace(/\s|\+/g, "")}?text=${encodeURIComponent(tr.whatsappMsgs.general)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1EBE57] rounded-full px-4 py-2 text-xs font-semibold"
                  >
                    <MessageCircle className="w-4 h-4" /> WhatsApp Sales
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contact Form */}
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="contact-form">
          <h3 className="font-display text-xl font-semibold text-[#0B132B]">{tr.contact.formTitle || (lang === "ar" ? "اترك رسالتك" : "Send an inquiry")}</h3>
          <div>
            <Label>{tr.quote.fName}</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="cf-name" required />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>{tr.quote.fEmail}</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="cf-email" />
            </div>
            <div>
              <Label>{tr.quote.fPhone}</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" data-testid="cf-phone" required />
            </div>
          </div>
          <div>
            <Label>{tr.quote.fMsg}</Label>
            <Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1" data-testid="cf-msg" required />
          </div>
          <Button type="submit" disabled={loading} className="rounded-full bg-[#1E5FD9] hover:bg-[#174AB0] px-6" data-testid="cf-submit">
            {loading ? tr.common.loading : tr.contact.send}
          </Button>
          <div className="pt-2 flex flex-wrap gap-3 text-xs text-slate-500">
            <a href={waLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-[#1E5FD9]">
              <MessageCircle className="w-3.5 h-3.5" /> {lang === "ar" ? "أو واتساب الآن" : "or chat via WhatsApp"}
            </a>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" /> Dubai Airport Freezone
            </span>
          </div>
        </form>
      </div>
    </div>
  );
}
